// Reflect & Act — React port of kula-create's reflect-act.html.
//
// Pedagogy: structured debrief. The fixed 3-stage shape (What / So What /
// Now What) forces movement from observation → interpretation → committed
// action. Stage 1 stays observational (no "why"); stage 3 demands specifics.
//
// Each stage submit fires onActivityResponse with
//   { stageId: 'what' | 'so-what' | 'now-what', responses: { [promptId]: value } }
// so the dual-write records one row per stage (activity_key = stage id).

import { useState, useCallback, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { ReflectActSlideData, Theme, ExperienceConclusion } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { Reveal } from '../../shared/Reveal';
import { ExperienceClose } from '../../shared/ExperienceClose';
import type { ActivityResponsePayload } from '../../../../../types';

type ReflectActStageId = 'what' | 'so-what' | 'now-what';

interface Props {
  data: ReflectActSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: ReflectActSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

export function ReflectActSlide({
  data,
  theme,
  isEditing,
  onUpdate,
  onActivityResponse,
  onComplete,
  savedResponse,
}: Props) {
  if (isEditing) {
    return <ReflectActEditor data={data} theme={theme} onUpdate={onUpdate} />;
  }
  return (
    <ReflectActStudent
      data={data}
      theme={theme}
      onActivityResponse={onActivityResponse}
      onComplete={onComplete}
      savedResponse={savedResponse}
    />
  );
}

// ---------------------------------------------------------------------------
// Student-facing renderer
// ---------------------------------------------------------------------------

function ReflectActStudent({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: ReflectActSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const hasStimulus = !!(data.stimulus && data.stimulus.text);

  // Saved payload is per-stage (last only) — only the final stage's id ('now-what')
  // proves the full reflection finished; an earlier stage's payload can't reconstruct what followed, so start fresh.
  const finalStageId = data.stages[data.stages.length - 1]?.id as ReflectActStageId | undefined;
  const restored = !!(finalStageId && savedResponse && savedResponse.stageId === finalStageId);
  const savedStageResponses =
    restored && savedResponse?.responses && typeof savedResponse.responses === 'object'
      ? (savedResponse.responses as Record<string, string>)
      : null;

  const [started, setStarted] = useState(!hasStimulus || restored);
  const [currentStage, setCurrentStage] = useState(restored ? data.stages.length - 1 : 0);
  const [stimulusOpen, setStimulusOpen] = useState(!restored);
  // responses per stage: { [stageId]: { [promptId]: value } }
  const [responses, setResponses] = useState<Record<ReflectActStageId, Record<string, string>>>({
    what: {},
    'so-what': {},
    'now-what': {},
    ...(restored && finalStageId && savedStageResponses ? { [finalStageId]: savedStageResponses } : {}),
  });
  const [completedStages, setCompletedStages] = useState<Set<ReflectActStageId>>(
    () => (restored ? new Set(data.stages.map((s) => s.id as ReflectActStageId)) : new Set()),
  );
  const [copied, setCopied] = useState(false);
  const completedRef = useRef(restored);

  const accent = theme.colors.accent || theme.colors.primary;
  const allComplete = data.stages.length > 0 && completedStages.size === data.stages.length;

  const setResponse = (stageId: ReflectActStageId, promptId: string, value: string) =>
    setResponses((prev) => ({
      ...prev,
      [stageId]: { ...prev[stageId], [promptId]: value },
    }));

  const isStageComplete = (stageIdx: number): boolean => {
    const stage = data.stages[stageIdx];
    if (!stage) return false;
    const stageResponses = responses[stage.id as ReflectActStageId] ?? {};
    return stage.prompts.every((p) => (stageResponses[p.id] ?? '').trim().length > 0);
  };

  const submitStage = useCallback(
    (stageIdx: number) => {
      const stage = data.stages[stageIdx];
      if (!stage) return;
      const stageId = stage.id as ReflectActStageId;
      const stageResponses = responses[stageId];

      onActivityResponse?.({
        activityType: 'reflect-act',
        mode: 'survey',
        responseData: { stageId, responses: stageResponses },
      });

      setCompletedStages((prev) => new Set(prev).add(stageId));
      if (stageIdx < data.stages.length - 1) {
        setCurrentStage(stageIdx + 1);
        // The stimulus is most useful during the observational first stage — collapse it after.
        if (stageIdx === 0) setStimulusOpen(false);
      } else if (!completedRef.current) {
        completedRef.current = true;
        onComplete?.();
      }
    },
    [data.stages, responses, onActivityResponse, onComplete],
  );

  const buildExportText = useCallback(() => {
    const lines: string[] = [data.title];
    data.stages.forEach((stage) => {
      lines.push('', `${stage.label}`);
      stage.prompts.forEach((p) => {
        lines.push(p.text, responses[stage.id as ReflectActStageId]?.[p.id] ?? '—', '');
      });
    });
    return lines.join('\n');
  }, [data.title, data.stages, responses]);

  const copyReflection = useCallback(() => {
    navigator.clipboard.writeText(buildExportText()).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  }, [buildExportText]);

  const downloadReflection = useCallback(() => {
    const blob = new Blob([buildExportText()], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'reflection.txt';
    a.click();
    URL.revokeObjectURL(url);
  }, [buildExportText]);

  // Close: foreground the "now what" commitment (the transferable output) and
  // show it back. reflect-act is inherently free-text (§11d exception), so the
  // close echoes the learner's words rather than reflecting a number.
  const nowWhatStage = data.stages.find((s) => s.id === 'now-what');
  const nowWhatEchoes = (nowWhatStage?.prompts ?? []).map((p) => ({
    label: p.text,
    value: responses['now-what']?.[p.id] ?? '',
  }));
  const nowWhatConclusion: ExperienceConclusion = data.conclusion ?? {
    heading: 'Your commitment',
    body: data.summary?.closingMessage || 'This is the action you committed to next.',
  };

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[720px] px-6 py-10">
        <header className="text-center mb-8">
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginBottom: 8 }}>
            {data.title}
          </h1>
          {data.subtitle && (
            <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>
          )}
        </header>

        {/* Stage indicator pills */}
        {started && (
          <div className="flex items-center gap-2 mb-8 text-xs">
            {data.stages.map((stage, idx) => {
              const isActive = idx === currentStage && !allComplete;
              const isDone = completedStages.has(stage.id as ReflectActStageId);
              return (
                <div key={stage.id} className="flex items-center gap-2 flex-1">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold transition-colors duration-300"
                    style={{
                      background: isActive ? accent : isDone ? `${accent}25` : theme.colors.surface,
                      color: isActive ? '#fff' : isDone ? accent : theme.colors.textMuted,
                    }}
                  >
                    {idx + 1} {stage.label}
                  </span>
                  {idx < data.stages.length - 1 && (
                    <div
                      className="flex-1 h-0.5 rounded transition-colors duration-500"
                      style={{ background: isDone ? accent : `${theme.colors.textMuted}33` }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Stimulus card */}
        {hasStimulus && !started && (
          <div
            className="border-l-4 rounded-r-lg py-5 px-6 mb-8"
            style={{ background: theme.colors.surface, borderColor: accent }}
          >
            <span className="inline-block text-xs font-bold tracking-wider uppercase mb-3" style={{ color: accent }}>
              Context
            </span>
            <p className="leading-relaxed text-base mb-2" style={{ color: theme.colors.text }}>
              {data.stimulus!.text}
            </p>
            {data.stimulus!.source && (
              <p className="text-xs italic mt-2" style={{ color: theme.colors.textMuted }}>
                {data.stimulus!.source}
              </p>
            )}
            <div className="mt-5">
              <button
                onClick={() => setStarted(true)}
                className="px-6 py-3 rounded-lg font-semibold text-sm"
                style={{ background: accent, color: '#fff' }}
              >
                Begin reflection
              </button>
            </div>
          </div>
        )}

        {/* Collapsible stimulus — stays available while reflecting (expanded by default during stage 1) */}
        {hasStimulus && started && (
          <div
            className="border-l-4 rounded-r-lg mb-8"
            style={{ background: theme.colors.surface, borderColor: accent }}
          >
            <button
              onClick={() => setStimulusOpen((o) => !o)}
              className="w-full flex items-center justify-between py-3 px-6 text-left"
            >
              <span className="text-xs font-bold tracking-wider uppercase" style={{ color: accent }}>
                Context
              </span>
              <span className="text-xs font-semibold" style={{ color: theme.colors.textMuted }}>
                {stimulusOpen ? 'Hide' : 'Show'}
              </span>
            </button>
            {stimulusOpen && (
              <div className="px-6 pb-4">
                <p className="leading-relaxed text-sm mb-2" style={{ color: theme.colors.text }}>
                  {data.stimulus!.text}
                </p>
                {data.stimulus!.source && (
                  <p className="text-xs italic" style={{ color: theme.colors.textMuted }}>
                    {data.stimulus!.source}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Stages */}
        {started && data.stages.map((stage, idx) => {
          if (idx > currentStage) return null;
          const stageId = stage.id as ReflectActStageId;
          const isDone = completedStages.has(stageId);
          const isCurrent = idx === currentStage && !allComplete;
          const ready = isStageComplete(idx);
          const isLast = idx === data.stages.length - 1;

          return (
            <section
              key={stage.id}
              className="mb-6"
              data-stage-id={stageId}
              style={{ opacity: !isCurrent && isDone ? 0.7 : 1 }}
            >
              <Reveal
                variant="up"
                className="rounded-lg p-6 border"
                style={{ background: theme.colors.surface, borderColor: `${accent}30` }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-sm"
                    style={{ background: accent, color: '#fff' }}
                  >
                    {idx + 1}
                  </span>
                  <h3 style={{ ...slideTypographyStyle(theme, 'subheading'), color: theme.colors.text, margin: 0 }}>
                    {stage.label}
                  </h3>
                </div>
                <p className="leading-relaxed text-sm mb-4" style={{ color: theme.colors.textMuted }}>
                  {stage.description}
                </p>

                <div className="space-y-4">
                  {stage.prompts.map((prompt) => {
                    const value = responses[stageId]?.[prompt.id] ?? '';
                    return (
                      <div key={prompt.id}>
                        <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text }}>
                          {prompt.text}
                        </label>
                        {prompt.inputType === 'select' ? (
                          <select
                            value={value}
                            disabled={isDone}
                            onChange={(e) => setResponse(stageId, prompt.id, e.target.value)}
                            className="w-full px-3 py-2 rounded border text-sm"
                            style={{
                              background: theme.colors.background,
                              borderColor: `${theme.colors.textMuted}33`,
                              color: theme.colors.text,
                            }}
                          >
                            <option value="">{prompt.placeholder ?? 'Select…'}</option>
                            {(prompt.options ?? []).map((opt, optIdx) => (
                              <option key={optIdx} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <>
                            <textarea
                              value={value}
                              disabled={isDone}
                              onChange={(e) => setResponse(stageId, prompt.id, e.target.value)}
                              placeholder={prompt.placeholder}
                              maxLength={prompt.maxLength ?? 400}
                              rows={3}
                              className="w-full px-3 py-2 rounded border text-sm"
                              style={{
                                background: theme.colors.background,
                                borderColor: `${theme.colors.textMuted}33`,
                                color: theme.colors.text,
                              }}
                            />
                            <div className="text-right text-xs mt-1" style={{ color: theme.colors.textMuted }}>
                              {value.length} / {prompt.maxLength ?? 400}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                {isCurrent && !isDone && (
                  <button
                    onClick={() => submitStage(idx)}
                    disabled={!ready}
                    className="mt-5 w-full px-6 py-3 rounded-lg font-semibold text-sm"
                    style={{
                      background: accent,
                      color: '#fff',
                      cursor: ready ? 'pointer' : 'not-allowed',
                      opacity: ready ? 1 : 0.4,
                    }}
                  >
                    {isLast ? 'Complete reflection' : 'Continue'}
                  </button>
                )}
              </Reveal>
            </section>
          );
        })}

        {/* Completion confirmation — always shown once every stage is submitted */}
        {allComplete && (
          <div
            className="rounded-lg p-4 mt-6 text-center"
            style={{ background: `${accent}15`, border: `1px solid ${accent}40` }}
          >
            <p className="text-sm font-semibold" style={{ color: accent }}>
              Reflection complete — all {data.stages.length} stages submitted.
            </p>
            {data.summary?.exportEnabled && (
              <div className="flex items-center justify-center gap-3 mt-3">
                <button
                  onClick={copyReflection}
                  className="px-4 py-2 rounded-lg font-semibold text-xs border"
                  style={{ borderColor: accent, color: accent, background: 'transparent' }}
                >
                  {copied ? 'Copied!' : 'Copy reflection'}
                </button>
                <button
                  onClick={downloadReflection}
                  className="px-4 py-2 rounded-lg font-semibold text-xs"
                  style={{ background: accent, color: '#fff' }}
                >
                  Download .txt
                </button>
              </div>
            )}
          </div>
        )}

        {/* Final summary — payoff register (heavier accent rule + larger radius) */}
        <AnimatePresence>
        {allComplete && data.summary?.showFullReflection && (
          <Reveal
            variant="scale"
            spring="bouncy"
            className="rounded-2xl border-l-[5px] shadow-sm p-6 mt-6"
            style={{ background: theme.colors.surface, borderColor: accent }}
          >
            <span className="inline-block text-xs font-bold tracking-wider uppercase mb-3" style={{ color: accent }}>
              Your full reflection
            </span>
            {data.stages.map((stage) => {
              const stageId = stage.id as ReflectActStageId;
              return (
                <div key={stage.id} className="mb-4 last:mb-0">
                  <h4 className="font-semibold text-sm mb-2" style={{ color: theme.colors.text }}>
                    {stage.label}
                  </h4>
                  {stage.prompts.map((p) => (
                    <div key={p.id} className="mb-2">
                      <p className="text-xs uppercase tracking-wide" style={{ color: theme.colors.textMuted }}>
                        {p.text}
                      </p>
                      <p className="text-sm whitespace-pre-wrap" style={{ color: theme.colors.text }}>
                        {responses[stageId]?.[p.id] ?? '—'}
                      </p>
                    </div>
                  ))}
                </div>
              );
            })}
          </Reveal>
        )}
        </AnimatePresence>

        {/* Authored close — echoes the Stage-3 "now what" commitment back so the
            debrief always produces something concrete (review §16), even when the
            full-reflection block is off. Folds the legacy closingMessage in. */}
        <AnimatePresence>
          {allComplete && (
            <Reveal variant="up" className="mt-6">
              <ExperienceClose
                conclusion={nowWhatConclusion}
                theme={theme}
                echoes={nowWhatEchoes}
              />
            </Reveal>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editing-mode renderer (3 fixed stages — what / so-what / now-what)
// ---------------------------------------------------------------------------

function ReflectActEditor({
  data,
  theme,
  onUpdate,
}: {
  data: ReflectActSlideData;
  theme: Theme;
  onUpdate: (data: ReflectActSlideData) => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;

  const updateField = <K extends keyof ReflectActSlideData>(key: K, value: ReflectActSlideData[K]) =>
    onUpdate({ ...data, [key]: value });

  const updateStage = (
    stageIdx: 0 | 1 | 2,
    patch: Partial<ReflectActSlideData['stages'][number]>,
  ) => {
    // Tuple type prevents direct index assignment — splice via array spread.
    const stages = [...data.stages] as Array<ReflectActSlideData['stages'][number]>;
    stages[stageIdx] = { ...stages[stageIdx], ...patch };
    onUpdate({
      ...data,
      stages: stages as unknown as ReflectActSlideData['stages'],
    });
  };

  const addPrompt = (stageIdx: 0 | 1 | 2) => {
    const stage = data.stages[stageIdx];
    updateStage(stageIdx, {
      prompts: [
        ...stage.prompts,
        { id: crypto.randomUUID(), text: '', inputType: 'text', maxLength: 400, placeholder: '' },
      ],
    });
  };

  const removePrompt = (stageIdx: 0 | 1 | 2, promptId: string) => {
    const stage = data.stages[stageIdx];
    updateStage(stageIdx, { prompts: stage.prompts.filter((p) => p.id !== promptId) });
  };

  const updatePrompt = (
    stageIdx: 0 | 1 | 2,
    promptId: string,
    patch: Partial<ReflectActSlideData['stages'][number]['prompts'][number]>,
  ) => {
    const stage = data.stages[stageIdx];
    updateStage(stageIdx, {
      prompts: stage.prompts.map((p) => (p.id === promptId ? { ...p, ...patch } : p)),
    });
  };

  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[720px]">
        <input
          value={data.title}
          onChange={(e) => updateField('title', e.target.value)}
          placeholder="Reflection title…"
          className="w-full mb-2 bg-transparent border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => updateField('subtitle', e.target.value)}
          placeholder="Subtitle"
          className="w-full mb-6 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm transition-colors"
          style={{ color: theme.colors.textMuted }}
        />

        <div
          className="rounded-lg border p-5 mb-6"
          style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}
        >
          <label className="block text-xs font-bold tracking-wider uppercase mb-2" style={{ color: accent }}>
            Stimulus (optional)
          </label>
          <textarea
            value={data.stimulus?.text ?? ''}
            onChange={(e) => {
              // Clear only the text — drop the whole stimulus object only when BOTH text and source are empty.
              const text = e.target.value;
              const source = data.stimulus?.source;
              updateField('stimulus', text || source ? { text, source } : undefined);
            }}
            placeholder="Stimulus text (the context learners reflect on)"
            rows={3}
            className="w-full mb-2 px-3 py-2 rounded border text-sm"
            style={{
              background: theme.colors.background,
              borderColor: `${theme.colors.textMuted}33`,
              color: theme.colors.text,
            }}
          />
          <input
            value={data.stimulus?.source ?? ''}
            onChange={(e) => {
              const source = e.target.value;
              const text = data.stimulus?.text ?? '';
              updateField('stimulus', text || source ? { text, source: source || undefined } : undefined);
            }}
            placeholder="Source attribution (optional)"
            className="w-full px-3 py-2 rounded border text-sm"
            style={{
              background: theme.colors.background,
              borderColor: `${theme.colors.textMuted}33`,
              color: theme.colors.text,
            }}
          />
        </div>

        {([0, 1, 2] as const).map((stageIdx) => {
          const stage = data.stages[stageIdx];
          return (
            <div
              key={stage.id}
              className="rounded-lg border p-5 mb-4"
              style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}
            >
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="inline-flex items-center justify-center w-6 h-6 rounded-full font-bold text-xs"
                  style={{ background: accent, color: '#fff' }}
                >
                  {stageIdx + 1}
                </span>
                <input
                  value={stage.label}
                  onChange={(e) => updateStage(stageIdx, { label: e.target.value })}
                  placeholder={`Stage label (${stage.id})`}
                  className="flex-1 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none font-semibold text-sm"
                  style={{ color: theme.colors.text }}
                />
              </div>
              <textarea
                value={stage.description}
                onChange={(e) => updateStage(stageIdx, { description: e.target.value })}
                placeholder="Stage description (what the learner does at this stage)"
                rows={2}
                className="w-full mb-4 px-3 py-2 rounded border text-sm"
                style={{
                  background: theme.colors.background,
                  borderColor: `${theme.colors.textMuted}33`,
                  color: theme.colors.text,
                }}
              />
              {stage.prompts.map((prompt) => (
                <div
                  key={prompt.id}
                  className="rounded p-3 mb-2"
                  style={{ background: theme.colors.background, border: `1px solid ${theme.colors.textMuted}22` }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      value={prompt.text}
                      onChange={(e) => updatePrompt(stageIdx, prompt.id, { text: e.target.value })}
                      placeholder="Prompt text"
                      className="flex-1 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
                      style={{ color: theme.colors.text }}
                    />
                    <select
                      value={prompt.inputType}
                      onChange={(e) =>
                        updatePrompt(stageIdx, prompt.id, {
                          inputType: e.target.value as 'text' | 'select',
                        })
                      }
                      className="text-xs px-2 py-1 rounded border"
                      style={{
                        background: theme.colors.background,
                        borderColor: `${theme.colors.textMuted}33`,
                        color: theme.colors.text,
                      }}
                    >
                      <option value="text">Text</option>
                      <option value="select">Select</option>
                    </select>
                    <button
                      onClick={() => removePrompt(stageIdx, prompt.id)}
                      className="text-xs text-gray-400 hover:text-red-500"
                    >
                      ×
                    </button>
                  </div>
                  {prompt.inputType === 'select' && (
                    <input
                      value={(prompt.options ?? []).join(', ')}
                      onChange={(e) =>
                        updatePrompt(stageIdx, prompt.id, {
                          options: e.target.value
                            .split(',')
                            .map((s) => s.trim())
                            .filter(Boolean),
                        })
                      }
                      placeholder="Comma-separated options"
                      className="w-full text-xs px-2 py-1 rounded border"
                      style={{
                        background: theme.colors.surface,
                        borderColor: `${theme.colors.textMuted}33`,
                        color: theme.colors.text,
                      }}
                    />
                  )}
                </div>
              ))}
              <button
                onClick={() => addPrompt(stageIdx)}
                className="text-xs"
                style={{ color: accent }}
              >
                + Add prompt
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
